"use client";

import LazyLoaded from "@/components/LazyLoaded";
import { useSafeQueryNavigation } from "@/hooks/navigation/useSafeQueryNavigation";
import { AccountType, Class, ClassRole, UserDetails } from "@knowt/syncing/graphql/schema";
import { useClass } from "@knowt/syncing/hooks/classes/useClass";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import { useParams, useSearchParams } from "next/navigation";
import React, { useMemo, useState } from "react";
import { UserContentManagementContextProvider } from "../../hooks/useUserContentManagementContext";
import { UserContentSortSettings } from "../../types";
import { CLASS_SETTINGS_TABS, ClassDashboardTab, ClassFilesTab } from "../types";
import { useSyncedState } from "@knowt/syncing/utils/hooks/useSyncedState";
import { BASE_URL } from "@/config/deployConstants";
import { copyToClipboard } from "@/utils/clipboard";
import dynamic from "next/dynamic";
import { ClassMemberWithDetails } from "@knowt/syncing/graphql/schema";

const ClassCodePopup = dynamic(() => import("../components/popups/ClassCodePopup"), {
    ssr: false,
});

const ClassPermissionsDeniedPopup = dynamic(
    () => import("../TeacherView/components/ClassDashboardStudentsTab/ClassPermissionsDeniedPopup"),
    {
        ssr: false,
    }
);

type ClassManagementContextProps = {
    //
    course: Class;
    classId: string;
    //
    isStudent: boolean;
    isTeacher: boolean;
    readOnly: boolean;
    //
    currentTab: ClassDashboardTab;
    changeCurrentTab: (newTab: ClassDashboardTab) => void;
    currentFilesTab: ClassFilesTab;
    setCurrentFilesTab: (newTab: ClassFilesTab) => void;
    defaultClassSettingsTab: CLASS_SETTINGS_TABS | null;
    setDefaultClassSettingsTab: (tab: CLASS_SETTINGS_TABS | null) => void;
    //
    selectedSection: string | null;
    setSelectedSection: (sectionId: string | null) => void;
    currentUserClassRole?: ClassRole;
    currentUserAccessibleSection?: string;
    setIsPermissionsDeniedPopupOpen: (isOpen: boolean) => void;
    openInviteToClassPopup: () => void;
    //
    students: ClassMemberWithDetails[];
};

const ClassManagementContext = createContext<ClassManagementContextProps | null>(null);

export const ClassManagementContextProvider = ({
    isListView,
    children,
    serverClass,
    serverUser,
    sortValue,
}: {
    isListView: boolean;
    children: React.ReactNode;
    serverClass: Class;
    serverUser: UserDetails;
    sortValue: UserContentSortSettings;
}) => {
    const searchParams = useSearchParams();
    const { classId } = useParams() as { classId: string };
    const { addParamsSilentReplace } = useSafeQueryNavigation();

    const { course: swrCourse, currentUserClassRole } = useClass({ classId: classId, fallbackData: serverClass });

    const userId = serverUser.ID as string;
    const course = swrCourse ?? serverClass;
    const isStudent = serverUser.accountType === AccountType.Student && !serverUser.classes?.enrolled?.length;
    const isTeacher = serverUser.accountType === AccountType.Teacher;

    const defaultCurrentTab =
        searchParams.get("tab") === "settings"
            ? ClassDashboardTab.SETTINGS
            : searchParams.get("tab") === "people"
              ? ClassDashboardTab.PEOPLE
              : searchParams.get("tab") === "materials"
                ? ClassDashboardTab.MATERIALS
                : ClassDashboardTab.MAIN;

    const [isInvitePopupOpen, setIsInvitePopupOpen] = useState(false);
    const [isPermissionsDeniedPopupOpen, setIsPermissionsDeniedPopupOpen] = useState(false);
    const [currentTab, setCurrentTab] = useSyncedState<ClassDashboardTab>(defaultCurrentTab);
    const [defaultClassSettingsTab, setDefaultClassSettingsTab] = useState<CLASS_SETTINGS_TABS | null>(
        (searchParams?.get("settingsTab") ?? null) as CLASS_SETTINGS_TABS | null
    );
    const [currentFilesTab, setCurrentFilesTab] = useState<ClassFilesTab>(ClassFilesTab.HOME);
    const [selectedSection, setSelectedSection] = useState<string | null>(null);

    const changeCurrentTab = (newTab: ClassDashboardTab) => {
        setCurrentTab(newTab);
        addParamsSilentReplace({ tab: newTab, section: undefined, itemId: undefined, itemType: undefined });
    };

    const readOnly = currentUserClassRole === ClassRole.STUDENT || currentUserClassRole === ClassRole.VIEWER;

    const currentUserAccessibleSection = useMemo(() => {
        if (currentUserClassRole === ClassRole.STUDENT)
            return course.members.find(member => member.userId === userId)?.sections?.[0];
    }, [course, currentUserClassRole, userId]);

    const students = useMemo(() => {
        return course?.members?.filter(
            member =>
                !member?.pending &&
                (!selectedSection || member?.sections.includes(selectedSection)) &&
                member?.role === ClassRole.STUDENT
        );
    }, [course, selectedSection]);

    const openInviteToClassPopup = () => {
        setIsInvitePopupOpen(true);
    };

    return (
        <ClassManagementContext.Provider
            value={{
                course,
                classId,
                readOnly,
                isStudent,
                isTeacher,
                currentTab,
                changeCurrentTab,
                currentFilesTab,
                setCurrentFilesTab,
                selectedSection,
                setSelectedSection,
                currentUserClassRole,
                setIsPermissionsDeniedPopupOpen,
                defaultClassSettingsTab,
                setDefaultClassSettingsTab,
                currentUserAccessibleSection,
                openInviteToClassPopup,
                students,
            }}>
            <UserContentManagementContextProvider
                isContentListView={isListView}
                serverUser={serverUser}
                sortValue={sortValue}
                classId={classId}>
                {children}
                <LazyLoaded load={isPermissionsDeniedPopupOpen}>
                    <ClassPermissionsDeniedPopup
                        isOpen={isPermissionsDeniedPopupOpen}
                        onClose={() => setIsPermissionsDeniedPopupOpen(false)}
                        role={currentUserClassRole}
                    />
                </LazyLoaded>

                <LazyLoaded load={isInvitePopupOpen}>
                    <ClassCodePopup
                        course={course}
                        isOpen={isInvitePopupOpen}
                        onClose={() => setIsInvitePopupOpen(false)}
                        onCopy={() => copyToClipboard(`${BASE_URL}/class/join?classId=${course.classId}`)}
                    />
                </LazyLoaded>
            </UserContentManagementContextProvider>
        </ClassManagementContext.Provider>
    );
};

export const useClassManagementContextSelector = <T,>(selector: (state: ClassManagementContextProps) => T) =>
    useContextSelector(ClassManagementContext, selector);
